import get from "lodash/get";
import merge from "lodash/merge";
import uniq from "lodash/uniq";

import { isMapping } from "src/formkit/components/types";
import type { OverrideConfig } from "./types";
import type { RelationshipHierarchy } from "@hightouch/formkit";
import isEmpty from "lodash/isEmpty";
import type { FieldValues } from "react-hook-form";

export const JOURNEY_SETTINGS_CONFIG_KEY = "journey_settings";

// Utility function to check if an object should be considered empty for mapping purposes
function isEmptyMappingObject(item: any): boolean {
  return (
    typeof item === "object" &&
    item !== null &&
    Object.keys(item).length === 1 &&
    Object.keys(item).some(
      // Ignore default mapping fields as [{type: "standard"}]
      (itemKey) => itemKey !== "to" && itemKey !== "from",
    )
  );
}

// Used to remove a mapping where the destination and source columns have not been selected
// For association mappings, custom mappings, mappings, etc.
export function cleanMappings(mappings) {
  const cleanedMappings: any[] = [];

  for (const mapping of mappings) {
    // For object inline mapper
    if (Array.isArray(mapping.from)) {
      const cleanedNestedMappings = cleanMappings(mapping.from);
      if (cleanedNestedMappings.length > 0) {
        cleanedMappings.push({ ...mapping, from: cleanedNestedMappings });
      }
      // For array inline mapper
    } else if (Array.isArray(mapping.children)) {
      const cleanedNestedMappings = cleanMappings(mapping.children);
      if (cleanedNestedMappings.length > 0) {
        cleanedMappings.push({ ...mapping, children: cleanedNestedMappings });
      }
    } else if (!Object.keys(mapping).every((key) => key === "type")) {
      cleanedMappings.push(mapping);
    }
  }

  return cleanedMappings;
}

export function cleanConfig(config) {
  for (const key in config) {
    const value = config[key];
    if (Array.isArray(value) && value.every(isMapping)) {
      config[key] = cleanMappings(value);
    } else if (value !== null && typeof value === "object") {
      cleanConfig(value);
    }

    // Special handling for journey_settings: if both mode and exit_config are null, set to an empty object
    if (
      key === JOURNEY_SETTINGS_CONFIG_KEY &&
      value !== null &&
      typeof value === "object"
    ) {
      const hasOnlyNullValues =
        value.mode === null &&
        (value.exit_config === null ||
          (typeof value.exit_config === "object" &&
            value.exit_config !== null &&
            value.exit_config.remove_after === null &&
            value.exit_config.remove_on_journey_exit === null));

      if (hasOnlyNullValues) {
        config[key] = {};
      }
    }
  }
  return config;
}

// We don't want to send back "REDACTED" values. Otherwise, we are saving/testing the new values as "REDACTED" and the test connection fails
export function cleanRedactedConfig(config) {
  const redactedFields = ["REDACTED"];

  for (const key in config) {
    if (redactedFields.includes(config[key])) {
      delete config[key];
    } else if (typeof config[key] === "object") {
      cleanRedactedConfig(config[key]);
    }
  }

  return config;
}

/**
 * Strip the properties with undefined values from the config
 */
export function keepDefined(config: any) {
  if (!config || typeof config !== "object") {
    return config;
  }

  return Object.fromEntries(
    Object.entries(config).filter(([, v]) => v !== undefined),
  );
}

/**
 * Returns all dependent nodes for the current key.
 */
export function getAllChildFields(
  key: string,
  relationshipHierarchy: RelationshipHierarchy | null,
  visited: Set<string> = new Set(),
): string[] {
  if (!relationshipHierarchy || visited.has(key)) {
    return [];
  }
  visited.add(key);
  const result: string[] = [];

  const children = get(relationshipHierarchy, [key, "children"], []);

  for (const child of children) {
    result.push(child);
  }

  const allChildFields = result.concat(
    result.flatMap((child) =>
      getAllChildFields(child, relationshipHierarchy, visited),
    ),
  );

  return allChildFields;
}

export function getUnlockedFields(
  overrideConfig: OverrideConfig | null | undefined,
  relationshipHierarchy: RelationshipHierarchy | null | undefined,
) {
  const result: string[] = [];

  if (!overrideConfig || !relationshipHierarchy) {
    return result;
  }

  for (const key of Object.keys(overrideConfig)) {
    if (get(overrideConfig, [key, "overridable"])) {
      result.push(key, ...getAllChildFields(key, relationshipHierarchy));
    }
  }

  return uniq(result);
}

/**
 * Check if there are any unlocked fields in the override config
 */
export function hasUnlockedFields(
  overrideConfig: OverrideConfig | null | undefined,
) {
  if (!overrideConfig) {
    return false;
  }

  for (const value of Object.values(overrideConfig)) {
    if (value.overridable) {
      return true;
    }
  }

  return false;
}

/**
 * Check if there are any null/empty required fields in the template config
 */
export function hasEmptyRequiredFields(config: Record<string, unknown>) {
  if (!config) {
    return false;
  }
  for (const value of Object.values(config)) {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    // Recurse into plain objects to catch nested null/undefined/"" leaves.
    // Arrays are excluded — empty arrays and arrays of default mapping rows
    // ({type: "standard"}) are valid template states, not "required field empty".
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      hasEmptyRequiredFields(value as Record<string, unknown>)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Helper function to process override_config entries
 */
function processOverrideEntries(
  overrides: OverrideConfig,
  keysSet: Set<string>,
  processor: (key: string, value: any) => any | null,
): Record<string, any> {
  return Object.entries(overrides).reduce(
    (acc, [key, value]) => {
      if (keysSet.has(key)) {
        const processed = processor(key, value);
        if (processed) {
          acc[key] = processed;
        }
      }
      return acc;
    },
    {} as Record<string, any>,
  );
}

/**
 * Clean the overrides by specifying only fields that are locked
 * If keys are not provided, all fields will be included
 */
export function cleanOverrides(
  overrides: OverrideConfig | null,
  keys?: string[],
  opts?: { onlyUnlocked?: boolean; setIsEmptyKey?: boolean },
): OverrideConfig | null {
  if (!overrides) {
    return null;
  }

  const { onlyUnlocked = false, setIsEmptyKey = false } = opts ?? {};
  const keysSet = new Set(keys ?? Object.keys(overrides));

  const overridesConfig = processOverrideEntries(
    overrides,
    keysSet,
    (_, value) => {
      if (typeof value.overridable !== "boolean") {
        return null;
      }
      if (onlyUnlocked && !value.overridable) {
        return null;
      }
      return { overridable: value.overridable };
    },
  );

  if (!setIsEmptyKey) {
    return overridesConfig as OverrideConfig;
  }

  const emptyValues = processOverrideEntries(overrides, keysSet, (_, value) => {
    if (typeof value.is_empty_in_template !== "boolean") {
      return null;
    }
    return {
      // This is the default, we'll rely on the actual
      // overridable value below in the merge.
      overridable: false,
      is_empty_in_template: value.is_empty_in_template,
    };
  });

  return merge(emptyValues, overridesConfig) as OverrideConfig;
}

/**
 * Check if a field is empty or undefined in the sync configuration
 */
export function isFieldEmpty(
  key: string,
  config: Record<string, any> | null | undefined,
): boolean {
  if (!config) {
    return false;
  }
  const value = Object.hasOwn(config, key) ? config[key] : get(config, key);
  // Handle numeric values including 0 - they are not empty
  if (typeof value === "number" && value !== undefined) {
    return false;
  }
  // Handle boolean values
  if (typeof value === "boolean" && value !== undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    const standardObjectValues = value.filter(
      (item) => !isEmptyMappingObject(item),
    );
    return standardObjectValues.length == 0;
  }
  return (
    value === null || value === undefined || value === "" || isEmpty(value)
  );
}

/**
 * Returns the associated empty value for the given value.
 */
export const getEmptyValue = (originalValue: any) => {
  if (Array.isArray(originalValue)) {
    return [];
  }
  if (originalValue !== null && typeof originalValue === "object") {
    return {};
  }
  return null;
};

/**
 * Helper function to check if any fields in a section have values
 */
export const hasFieldValues = (
  fieldKeys: string[],
  formValues: FieldValues,
  context?: { showClearable?: boolean },
): boolean => {
  return fieldKeys.some((key) => {
    // Use isFieldEmpty for basic empty checks
    if (isFieldEmpty(key, formValues)) {
      return false;
    }

    const value = get(formValues, key);
    if (Array.isArray(value)) {
      const standardObjectValues = value.filter(
        (item) => !isEmptyMappingObject(item),
      );
      return standardObjectValues.length > 0;
    }
    if (isEmptyMappingObject(value)) {
      return false;
    }

    // In template context, for empty objects and arrays, consider them as defaults
    if (context?.showClearable) {
      if (
        (typeof value === "object" &&
          value !== null &&
          Object.keys(value).length === 0) ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return false;
      }
    }

    return true;
  });
};
